import { jsPDF } from 'jspdf';
import { buildFrameSpec, renderFramedCanvas, renderFramedSvg } from './qrFrames';
import { buildQrSvg } from './qrSvg';
import { BODY_SHAPES, EYE_BALL_SHAPES, EYE_FRAME_SHAPES } from './qrShapes';
import { encodeCanvas, extForBlob } from '../shared/imageOutputFormats';

export const CONTENT_TYPES = [
    'url',
    'text',
    'email',
    'phone',
    'sms',
    'wifi',
    'vcard',
    'mecard',
    'location',
    'event',
    'payment',
    'applink',
    'whatsapp',
    'facebook',
    'instagram',
    'twitter',
    'youtube',
    'linkedin',
    'telegram',
    'crypto',
];

/** Coin schemes for the crypto payment type (BIP-21 style URIs). */
export const CRYPTO_COINS = ['bitcoin', 'ethereum', 'litecoin', 'dogecoin', 'bitcoincash'];

/** Mobile-money providers for the payment type. */
export const PAYMENT_PROVIDERS = ['bkash', 'nagad', 'rocket', 'upay', 'upi'];

/** Decorative backdrops drawn behind the modules at 10% opacity. */
export const BG_PATTERNS = ['none', 'dots', 'grid', 'diagonal'];

export const SIZE_OPTIONS = [300, 500, 600, 1000, 1500, 2000];

export const FORMAT_OPTIONS = ['png', 'jpg', 'svg', 'pdf'];

/** Shape catalogues, in the order they appear in the pickers. */
export const DOT_STYLES = BODY_SHAPES;
export const EYE_FRAME_STYLES = EYE_FRAME_SHAPES;
export const EYE_BALL_STYLES = EYE_BALL_SHAPES;

export const ERROR_LEVELS = ['L', 'M', 'Q', 'H'];

export const GRADIENT_TYPES = ['linear', 'radial'];

const WIFI_ENCRYPTION = {
    wpa: 'WPA',
    wep: 'WEP',
    nopass: 'nopass',
};

export const DEFAULT_DESIGN = {
    fgColor: '#000000',
    bgColor: '#ffffff',
    transparentBg: false,
    gradientEnabled: false,
    gradientType: 'linear',
    gradientFrom: '#F97316',
    gradientTo: '#DB2777',
    gradientRotation: 0,
    dotStyle: 'square',
    eyeFrameStyle: 'square',
    eyeBallStyle: 'square',
    customEyeColors: false,
    eyeFrameColor: '#000000',
    eyeBallColor: '#000000',
    logoId: 'none',
    logoDataUrl: null,
    logoSizePercent: 25,
    logoMarginPercent: 1.5,
    logoRadiusPercent: 0,
    logoPlate: true,
    logoBgColor: '#000000',
    logoIconColor: '#ffffff',
    hideBackgroundDots: true,
    errorLevel: 'H',
    quietZonePercent: 4,
    cornerRadiusPercent: 0,
    bgPattern: 'none',
    bgImageDataUrl: null,
    bgImageOpacity: 20,
    frameType: 'none',
    frameLabel: 'Scan Me',
    frameBorderColor: '#000000',
    frameLabelBgColor: '#000000',
    frameLabelTextColor: '#ffffff',
    frameBgColor: '#ffffff',
};

function escapeVcard(value) {
    return String(value || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/\n/g, '\\n');
}

function withHttps(url) {
    const trimmed = (url || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}

/**
 * Social fields accept either a full profile link or a bare handle, so a user
 * can paste whatever they have. `@name` and `name` both become a profile URL.
 */
function profileUrl(value, base) {
    const trimmed = (value || '').trim().replace(/^@/, '');
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed) || /^[\w-]+\.[a-z]{2,}\//i.test(trimmed)) return withHttps(trimmed);
    return `${base}${encodeURIComponent(trimmed)}`;
}

function escapeMecard(value) {
    return String(value || '').replace(/([\\;:,])/g, '\\$1');
}

/** `2026-08-25T10:30` (datetime-local) → `20260825T103000`, the vEvent format. */
function toCalendarStamp(value) {
    const trimmed = (value || '').trim();
    if (!trimmed) return '';
    const digits = trimmed.replace(/[-:]/g, '');
    const [date, time = ''] = digits.split('T');
    if (!date) return '';
    return `${date}T${(time + '000000').slice(0, 6)}`;
}

export function buildQrPayload(type, fields) {
    switch (type) {
        case 'url':
            return withHttps(fields.url);
        case 'text':
            return (fields.text || '').trim();
        case 'wifi': {
            const ssid = (fields.ssid || '').trim();
            if (!ssid) return '';
            const enc = WIFI_ENCRYPTION[fields.encryption] || 'WPA';
            const password = (fields.password || '').trim();
            if (enc === 'nopass') return `WIFI:T:nopass;S:${ssid};;`;
            return `WIFI:T:${enc};S:${ssid};P:${password};;`;
        }
        case 'vcard': {
            const first = (fields.firstName || '').trim();
            const last = (fields.lastName || '').trim();
            const full = [first, last].filter(Boolean).join(' ').trim();
            if (!full && !(fields.phone || '').trim() && !(fields.email || '').trim()) return '';
            const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
            if (last || first) lines.push(`N:${escapeVcard(last)};${escapeVcard(first)}`);
            if (full) lines.push(`FN:${escapeVcard(full)}`);
            if ((fields.phone || '').trim()) lines.push(`TEL:${escapeVcard(fields.phone)}`);
            if ((fields.email || '').trim()) lines.push(`EMAIL:${escapeVcard(fields.email)}`);
            if ((fields.organization || '').trim()) lines.push(`ORG:${escapeVcard(fields.organization)}`);
            if ((fields.jobTitle || '').trim()) lines.push(`TITLE:${escapeVcard(fields.jobTitle)}`);
            if ((fields.website || '').trim()) lines.push(`URL:${escapeVcard(withHttps(fields.website))}`);
            if ((fields.address || '').trim()) lines.push(`ADR:;;${escapeVcard(fields.address)};;;;`);
            lines.push('END:VCARD');
            return lines.join('\n');
        }
        case 'email': {
            const to = (fields.emailTo || '').trim();
            if (!to) return '';
            const params = new URLSearchParams();
            if ((fields.emailSubject || '').trim()) params.set('subject', fields.emailSubject.trim());
            if ((fields.emailBody || '').trim()) params.set('body', fields.emailBody.trim());
            const query = params.toString();
            return query ? `mailto:${to}?${query}` : `mailto:${to}`;
        }
        case 'phone': {
            const phone = (fields.phoneNumber || '').trim();
            return phone ? `tel:${phone}` : '';
        }
        case 'sms': {
            const number = (fields.smsNumber || '').trim();
            if (!number) return '';
            const body = (fields.smsMessage || '').trim();
            return body ? `sms:${number}?body=${encodeURIComponent(body)}` : `sms:${number}`;
        }
        case 'mecard': {
            const first = (fields.firstName || '').trim();
            const last = (fields.lastName || '').trim();
            if (!first && !last && !(fields.phone || '').trim() && !(fields.email || '').trim()) return '';
            const parts = [];
            if (first || last) parts.push(`N:${escapeMecard(last)},${escapeMecard(first)}`);
            if ((fields.phone || '').trim()) parts.push(`TEL:${escapeMecard(fields.phone)}`);
            if ((fields.email || '').trim()) parts.push(`EMAIL:${escapeMecard(fields.email)}`);
            if ((fields.organization || '').trim()) parts.push(`ORG:${escapeMecard(fields.organization)}`);
            if ((fields.website || '').trim()) parts.push(`URL:${escapeMecard(withHttps(fields.website))}`);
            if ((fields.address || '').trim()) parts.push(`ADR:${escapeMecard(fields.address)}`);
            return `MECARD:${parts.join(';')};;`;
        }
        case 'location': {
            const lat = (fields.latitude || '').trim();
            const lng = (fields.longitude || '').trim();
            const address = (fields.locationAddress || '').trim();
            // An address alone opens a Maps search; coordinates win when given,
            // because they pin the exact spot.
            if (!lat || !lng) {
                return address
                    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
                    : '';
            }
            const label = (fields.locationLabel || '').trim() || address;
            return label ? `geo:${lat},${lng}?q=${lat},${lng}(${encodeURIComponent(label)})` : `geo:${lat},${lng}`;
        }
        case 'payment': {
            const account = (fields.paymentNumber || '').trim();
            if (!account) return '';
            const provider = PAYMENT_PROVIDERS.includes(fields.paymentProvider) ? fields.paymentProvider : 'bkash';
            const amount = (fields.paymentAmount || '').trim();
            const note = (fields.paymentNote || '').trim();
            // UPI has a real payment URI; the Bangladeshi wallets do not publish
            // one, so those codes carry readable text the payer types into the app.
            if (provider === 'upi') {
                const params = new URLSearchParams({ pa: account, cu: 'INR' });
                if (amount) params.set('am', amount);
                if (note) params.set('tn', note);
                return `upi://pay?${params.toString()}`;
            }
            const names = { bkash: 'bKash', nagad: 'Nagad', rocket: 'Rocket', upay: 'Upay' };
            return [
                `${names[provider] || 'Payment'} — ${account}`,
                amount ? `Amount: ${amount} BDT` : '',
                note,
            ].filter(Boolean).join(' | ');
        }
        case 'applink': {
            const ios = withHttps(fields.appIosUrl);
            const android = withHttps(fields.appAndroidUrl);
            // One store link encodes directly; there is no reliable way to branch
            // by platform inside a QR code without a redirect service.
            return android || ios;
        }
        case 'event': {
            const title = (fields.eventTitle || '').trim();
            if (!title) return '';
            const start = toCalendarStamp(fields.eventStart);
            const end = toCalendarStamp(fields.eventEnd);
            const lines = ['BEGIN:VEVENT', `SUMMARY:${escapeVcard(title)}`];
            if ((fields.eventLocation || '').trim()) lines.push(`LOCATION:${escapeVcard(fields.eventLocation)}`);
            if ((fields.eventDescription || '').trim()) lines.push(`DESCRIPTION:${escapeVcard(fields.eventDescription)}`);
            if (start) lines.push(`DTSTART:${start}`);
            if (end) lines.push(`DTEND:${end}`);
            lines.push('END:VEVENT');
            return lines.join('\n');
        }
        case 'whatsapp': {
            const number = (fields.whatsappNumber || '').trim().replace(/[^\d]/g, '');
            if (!number) return '';
            const message = (fields.whatsappMessage || '').trim();
            return message ? `https://wa.me/${number}?text=${encodeURIComponent(message)}` : `https://wa.me/${number}`;
        }
        case 'facebook':
            return profileUrl(fields.facebookUser, 'https://facebook.com/');
        case 'instagram':
            return profileUrl(fields.instagramUser, 'https://instagram.com/');
        case 'twitter':
            return profileUrl(fields.twitterUser, 'https://x.com/');
        case 'linkedin':
            return profileUrl(fields.linkedinUser, 'https://linkedin.com/in/');
        case 'telegram':
            return profileUrl(fields.telegramUser, 'https://t.me/');
        case 'youtube':
            return withHttps(fields.youtubeUrl);
        case 'crypto': {
            const address = (fields.cryptoAddress || '').trim();
            if (!address) return '';
            const coin = CRYPTO_COINS.includes(fields.cryptoCoin) ? fields.cryptoCoin : 'bitcoin';
            const params = new URLSearchParams();
            if ((fields.cryptoAmount || '').trim()) params.set('amount', fields.cryptoAmount.trim());
            if ((fields.cryptoLabel || '').trim()) params.set('label', fields.cryptoLabel.trim());
            if ((fields.cryptoMessage || '').trim()) params.set('message', fields.cryptoMessage.trim());
            const query = params.toString();
            return query ? `${coin}:${address}?${query}` : `${coin}:${address}`;
        }
        default:
            return '';
    }
}

export function isQrPayloadValid(type, fields) {
    return buildQrPayload(type, fields).length > 0;
}

/* ------------------------------------------------------------------ *
 * Validation                                                          *
 * ------------------------------------------------------------------ */

const URL_RE = /^(https?:\/\/)?(([\w-]+\.)+[a-z]{2,}|localhost|(\d{1,3}\.){3}\d{1,3})(:\d+)?([/?#]\S*)?$/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
const HANDLE_RE = /^[A-Za-z0-9._-]{2,40}$/;
const CRYPTO_RE = /^[A-Za-z0-9]{20,120}$/;

/** Byte budget per error-correction level (mode-8 data). */
const CAPACITY = { L: 2953, M: 2331, Q: 1663, H: 1273 };

const filled = (value) => !!String(value ?? '').trim();
const digitsOf = (value) => String(value ?? '').replace(/\D/g, '');

function isUrl(value) {
    return URL_RE.test(String(value).trim());
}

function isPhone(value) {
    const digits = digitsOf(value);
    return /^\+?[\d\s().-]+$/.test(String(value).trim()) && digits.length >= 6 && digits.length <= 15;
}

function isHandleOrUrl(value) {
    const trimmed = String(value).trim().replace(/^@/, '');
    return HANDLE_RE.test(trimmed) || isUrl(trimmed);
}

/** Host of a URL that may arrive without a protocol. */
function hostOf(value) {
    return String(value).trim()
        .replace(/^[a-z]+:\/\//i, '')
        .split(/[/?#]/)[0]
        .toLowerCase();
}

function isYoutubeHost(value) {
    return /(^|\.)(youtube\.com|youtu\.be)$/.test(hostOf(value));
}

function coordError(value, limit) {
    const num = Number(String(value).trim());
    if (!Number.isFinite(num)) return 'number';
    return Math.abs(num) > limit ? 'range' : null;
}

/**
 * Field-level checks for the active content type.
 *
 * Only *filled* fields are judged — an empty form is "not ready yet", not
 * "wrong" — except where one field becomes required because a sibling was
 * filled in (an event needs a name, WiFi needs a password once secured).
 *
 * @param {string} errorLevel selected error-correction level, which sets the byte budget
 * @returns {Record<string,string>} field key → error code (empty when valid)
 */
export function validateQrContent(type, fields, errorLevel = 'H') {
    const errors = {};
    const bad = (key, code) => { errors[key] = code; };

    switch (type) {
        case 'url':
            if (filled(fields.url) && !isUrl(fields.url)) bad('url', 'url');
            break;
        case 'text':
            if (String(fields.text || '').length > 2000) bad('text', 'tooLong');
            break;
        case 'email':
            if (filled(fields.emailTo) && !EMAIL_RE.test(fields.emailTo.trim())) bad('emailTo', 'email');
            break;
        case 'phone':
            if (filled(fields.phoneNumber) && !isPhone(fields.phoneNumber)) bad('phoneNumber', 'phone');
            break;
        case 'sms':
            if (filled(fields.smsNumber) && !isPhone(fields.smsNumber)) bad('smsNumber', 'phone');
            break;
        case 'wifi':
            if (filled(fields.ssid) && fields.ssid.trim().length > 32) bad('ssid', 'ssid');
            if (filled(fields.ssid) && fields.encryption !== 'nopass' && !filled(fields.password)) bad('password', 'required');
            break;
        case 'vcard':
        case 'mecard':
            if (filled(fields.email) && !EMAIL_RE.test(fields.email.trim())) bad('email', 'email');
            if (filled(fields.phone) && !isPhone(fields.phone)) bad('phone', 'phone');
            if (filled(fields.website) && !isUrl(fields.website)) bad('website', 'url');
            break;
        case 'location': {
            const latError = filled(fields.latitude) ? coordError(fields.latitude, 90) : null;
            const lngError = filled(fields.longitude) ? coordError(fields.longitude, 180) : null;
            if (latError) bad('latitude', latError === 'range' ? 'latRange' : 'number');
            if (lngError) bad('longitude', lngError === 'range' ? 'lngRange' : 'number');
            // One coordinate alone is not a location — unless an address carries it.
            if (filled(fields.latitude) !== filled(fields.longitude)) {
                bad(filled(fields.latitude) ? 'longitude' : 'latitude', 'required');
            }
            break;
        }
        case 'payment':
            if (filled(fields.paymentNumber) && fields.paymentProvider !== 'upi'
                && !isPhone(fields.paymentNumber)) bad('paymentNumber', 'phone');
            if (filled(fields.paymentAmount) && !(Number(fields.paymentAmount) > 0)) bad('paymentAmount', 'amount');
            break;
        case 'applink':
            if (filled(fields.appIosUrl) && !isUrl(fields.appIosUrl)) bad('appIosUrl', 'url');
            if (filled(fields.appAndroidUrl) && !isUrl(fields.appAndroidUrl)) bad('appAndroidUrl', 'url');
            break;
        case 'event': {
            const hasDetails = filled(fields.eventLocation) || filled(fields.eventStart)
                || filled(fields.eventEnd) || filled(fields.eventDescription);
            if (hasDetails && !filled(fields.eventTitle)) bad('eventTitle', 'required');
            if (filled(fields.eventStart) && filled(fields.eventEnd)
                && fields.eventEnd.trim() < fields.eventStart.trim()) bad('eventEnd', 'endBeforeStart');
            break;
        }
        case 'whatsapp': {
            if (filled(fields.whatsappNumber)) {
                const digits = digitsOf(fields.whatsappNumber);
                if (digits.length < 8 || digits.length > 15) bad('whatsappNumber', 'whatsapp');
            }
            break;
        }
        case 'youtube':
            if (filled(fields.youtubeUrl)) {
                if (!isUrl(fields.youtubeUrl)) bad('youtubeUrl', 'url');
                else if (!isYoutubeHost(fields.youtubeUrl)) bad('youtubeUrl', 'youtube');
            }
            break;
        case 'facebook':
        case 'instagram':
        case 'twitter':
        case 'linkedin':
        case 'telegram': {
            const key = `${type}User`;
            if (filled(fields[key]) && !isHandleOrUrl(fields[key])) bad(key, 'handle');
            break;
        }
        case 'crypto':
            if (filled(fields.cryptoAddress) && !CRYPTO_RE.test(fields.cryptoAddress.trim().replace(/^0x/i, ''))) {
                bad('cryptoAddress', 'walletAddress');
            }
            if (filled(fields.cryptoAmount) && !(Number(fields.cryptoAmount) > 0)) bad('cryptoAmount', 'amount');
            break;
        default:
            break;
    }

    // Whatever the type, the payload has to fit the chosen error-correction level.
    const payload = buildQrPayload(type, fields);
    if (payload) {
        const bytes = new TextEncoder().encode(payload).length;
        const limit = CAPACITY[errorLevel] || CAPACITY.H;
        if (bytes > limit) errors._payload = 'capacity';
    }

    return errors;
}

const FRAME_OPTS = (design) => ({
    label: design.frameLabel,
    borderColor: design.frameBorderColor,
    labelBgColor: design.frameLabelBgColor,
    labelTextColor: design.frameLabelTextColor,
    bgColor: design.frameBgColor,
});

/** SVG string for the code, already wrapped in its frame when one is picked. */
export function renderQrSvgString(payload, design, size) {
    const qrSvg = buildQrSvg(payload, design, size);
    const spec = buildFrameSpec(design.frameType, size, FRAME_OPTS(design));
    return spec ? renderFramedSvg(spec, qrSvg) : qrSvg;
}

export function svgToDataUrl(svg) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image load failed'));
        img.src = src;
    });
}

/**
 * Rasterise the QR (plus frame) at `size`.
 * The frame is drawn on the canvas rather than rasterised from the wrapped SVG,
 * so its text uses the real system font instead of the SVG font fallback.
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function renderQrCanvas(payload, design, size) {
    const img = await loadImage(svgToDataUrl(buildQrSvg(payload, design, size)));
    const spec = buildFrameSpec(design.frameType, size, FRAME_OPTS(design));

    const canvas = document.createElement('canvas');
    if (!spec) {
        canvas.width = size;
        canvas.height = size;
        canvas.getContext('2d').drawImage(img, 0, 0, size, size);
        return canvas;
    }
    return renderFramedCanvas(spec, img, canvas);
}

/** Live preview artwork — SVG data URL, so it stays crisp at any zoom. */
export async function renderPreview(payload, design, size = 512) {
    const svg = renderQrSvgString(payload, design, size);
    // Round-trips through an <img> so a malformed shape fails here, not silently
    // in the preview.
    const img = await loadImage(svgToDataUrl(svg));
    return {
        previewUrl: svgToDataUrl(svg),
        width: img.naturalWidth || size,
        height: img.naturalHeight || size,
    };
}

function flattenOnBackground(canvas, bgColor) {
    const out = document.createElement('canvas');
    out.width = canvas.width;
    out.height = canvas.height;
    const ctx = out.getContext('2d');
    ctx.fillStyle = bgColor || '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(canvas, 0, 0);
    return out;
}

/** Print resolution for PDF export — the chosen pixel size at 300 DPI. */
const PDF_DPI = 300;

/** Build the downloadable file for one format at the chosen size. */
export async function exportQrCode(payload, design, { size, format }) {
    // The one format that is not a picture of the code but the code itself.
    if (format === 'svg') {
        return new Blob([renderQrSvgString(payload, design, size)], { type: 'image/svg+xml' });
    }

    const canvas = await renderQrCanvas(payload, design, size);
    const background = design.transparentBg ? '#ffffff' : design.bgColor;

    if (format !== 'pdf') {
        // PNG, JPG, WebP, AVIF — the image tools' encoder, which lays the
        // formats without transparency onto the background itself and falls
        // back to WebAssembly where the canvas cannot write an AVIF.
        const { blob } = await encodeCanvas(canvas, format, { background });
        return blob;
    }

    const flat = flattenOnBackground(canvas, background);
    // The page is sized so the artwork prints at PDF_DPI — jsPDF's own `px`
    // unit would stretch a 1500px code across a 70cm page at ~54 DPI.
    const ptWidth = (flat.width / PDF_DPI) * 72;
    const ptHeight = (flat.height / PDF_DPI) * 72;
    const pdf = new jsPDF({
        unit: 'pt',
        format: [ptWidth, ptHeight],
        orientation: ptWidth >= ptHeight ? 'landscape' : 'portrait',
    });
    pdf.addImage(flat.toDataURL('image/png'), 'PNG', 0, 0, ptWidth, ptHeight);
    return pdf.output('blob');
}

/**
 * `qr-code.png`. A picture is named after what actually came back rather than
 * what was asked for: AVIF comes back as WebP on a browser that can write
 * neither, and an `.avif` holding a WebP is a file nothing will open.
 */
export function outputFileName(format, blob) {
    return `qr-code.${blob ? extForBlob(blob, format) : format}`;
}

export function isSupportedLogoFile(file) {
    if (!file) return false;
    const name = (file.name || '').toLowerCase();
    return file.type === 'image/png'
        || file.type === 'image/svg+xml'
        || file.type === 'image/jpeg'
        || file.type === 'image/webp'
        || /\.(png|svg|jpe?g|webp)$/i.test(name);
}

export function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}
